import { create } from 'zustand';
import onboardingService from '../api/services/onboarding.service';
import profileService from '../api/services/profile.service';
import eventsService from '../api/services/events.service';
import useAuthStore from './useAuthStore';
import { getTokenSubject } from '../utils/token';

export const SIGNUP_STEPS = ['profile', 'interests', 'kids', 'schools', 'class'];
export const MAX_INTERESTS = 10;

/* Draft state for the five-step sign-up wizard a brand-new account lands on
   after OTP verification. Nothing reaches the API until Finish Setup: the whole
   profile goes up in one multipart PATCH, then the class group is created. */
const EMPTY = {
  step: 0,
  firstName: '',
  lastName: '',
  zipCode: '',
  /* 'idle' | 'checking' | 'found' | 'notfound' — drives the address line and the
     red border under the ZIP field */
  zipStatus: 'idle',
  role: '',
  photo: null,
  photoPreview: '',
  /* { name, address, latitude, longitude } — geocoded from the zip code */
  location: null,
  /* interest ids; `tags` (emoji + label) is what the API stores */
  activities: [],
  kids: [],
  /* grade-level class networkIds the parent joins */
  joinGroups: [],
  /* the schools those grades belong to, kept for the last step's labels */
  selectedGroups: [],
  roomParentGrade: '',
  /* set when joining a class group that already exists under that grade —
     then className isn't needed and isn't asked for */
  joinClassId: '',
  className: '',
  /* the profile PATCH succeeded but the class step didn't — a retry of Finish
     Setup must not send the profile a second time */
  profileSaved: false,
  /* the class group that got created or joined, so a retry doesn't make a
     second one */
  classNetworkId: '',
};

const useSignupStore = create((set, get) => ({
  ...EMPTY,

  activityCategories: [],
  activitiesLoading: false,
  schools: [],
  schoolsLoading: false,
  /* existing class groups per grade networkId, fetched on demand */
  gradeClasses: {},
  classesLoading: false,
  submitting: false,

  set: (patch) => set(patch),

  /* Photo previews are object URLs held in this store, so clearing the draft is
     also where they're released. */
  reset: () => {
    const { photoPreview, kids } = get();
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    kids.forEach((k) => k.preview && URL.revokeObjectURL(k.preview));
    set({ ...EMPTY, activityCategories: [], schools: [], gradeClasses: {} });
  },

  goNext: () => set((s) => ({ step: Math.min(s.step + 1, SIGNUP_STEPS.length - 1) })),
  goBack: () => set((s) => ({ step: Math.max(s.step - 1, 0) })),

  /* The schools list is ranked by distance from the parent, so the zip has to
     become a point first. This is the same Places proxy the event form searches
     addresses with; its first hit for a bare zip is that zip's centroid. */
  lookupZip: async (zip) => {
    set({ zipStatus: 'checking' });
    try {
      const [first] = await eventsService.searchAddress(zip);
      if (!first) {
        set({ location: null, zipStatus: 'notfound' });
        return null;
      }
      const location = {
        name: first.name,
        address: first.address,
        latitude: first.lat,
        longitude: first.lng,
      };
      set({ location, zipStatus: 'found' });
      return location;
    } catch {
      /* a failed lookup reads the same as an unknown zip — the field goes red
         and they can retype to try again */
      set({ location: null, zipStatus: 'notfound' });
      return null;
    }
  },

  loadActivities: async () => {
    if (get().activityCategories.length || get().activitiesLoading) return;
    set({ activitiesLoading: true });
    try {
      set({ activityCategories: await profileService.activities() });
    } finally {
      set({ activitiesLoading: false });
    }
  },

  loadSchools: async () => {
    const { location, schoolsLoading } = get();
    if (!location || schoolsLoading) return;
    set({ schoolsLoading: true });
    try {
      const { schools } = await onboardingService.schoolsNear({
        lat: location.latitude,
        lng: location.longitude,
        limit: 30,
      });
      set({ schools });
    } finally {
      set({ schoolsLoading: false });
    }
  },

  toggleActivity: (id) =>
    set((s) => {
      if (s.activities.includes(id)) return { activities: s.activities.filter((a) => a !== id) };
      if (s.activities.length >= MAX_INTERESTS) return {};
      return { activities: [...s.activities, id] };
    }),

  addKid: (kid) => set((s) => ({ kids: [...s.kids, kid] })),
  removeKid: (id) =>
    set((s) => {
      const gone = s.kids.find((k) => k.id === id);
      if (gone?.preview) URL.revokeObjectURL(gone.preview);
      return { kids: s.kids.filter((k) => k.id !== id) };
    }),

  toggleGrade: (networkId) =>
    set((s) => {
      const joinGroups = s.joinGroups.includes(networkId)
        ? s.joinGroups.filter((g) => g !== networkId)
        : [...s.joinGroups, networkId];
      /* dropping the grade the parent said they're a room parent for clears that
         answer too — and the class they picked under it — so the last step
         can't point at a group they never joined */
      const keep = joinGroups.includes(s.roomParentGrade);
      return {
        joinGroups,
        roomParentGrade: keep ? s.roomParentGrade : '',
        joinClassId: keep ? s.joinClassId : '',
      };
    }),

  /* Class groups already set up under a grade — offered so a second room parent
     joins the existing class instead of creating a duplicate of it. Fetched once
     per grade and kept, since picking between grades is a back-and-forth. */
  loadClassesForGrade: async (gradeNetworkId) => {
    if (!gradeNetworkId || get().gradeClasses[gradeNetworkId]) return;
    set({ classesLoading: true });
    try {
      const classes = await onboardingService.classesForGrade(gradeNetworkId);
      set((s) => ({ gradeClasses: { ...s.gradeClasses, [gradeNetworkId]: classes } }));
    } finally {
      set({ classesLoading: false });
    }
  },

  /* Grades the parent joined, with their school's name — the last step lists
     these as the classes they could be a room parent for. */
  joinedGrades: () => {
    const { schools, joinGroups } = get();
    return schools.flatMap((s) =>
      s.grades.filter((g) => joinGroups.includes(g.networkId)).map((g) => ({ ...g, school: s.name }))
    );
  },

  /* Finishes the account: profile + interests + kids + group joins in one PATCH,
     then the class group — created, or joined when one is already there. Both
     halves have to land before the portal has anything to show, so a failure
     leaves the wizard where it is. */
  submit: async () => {
    const s = get();
    set({ submitting: true });
    try {
      if (!s.profileSaved) {
        const byId = new Map();
        s.activityCategories.forEach((c) => c.items.forEach((i) => byId.set(i.id, i)));
        const picked = s.activities.map((id) => byId.get(id)).filter(Boolean);
        await profileService.saveParentProfile({
          firstName: s.firstName.trim(),
          lastName: s.lastName.trim(),
          zipCode: s.zipCode.trim(),
          role: s.role,
          location: s.location,
          tags: picked.map((i) => `${i.emoji || ''} ${i.label}`.replace(/\s+/g, ' ').trim()),
          hobbies: picked.map((i) => i.label).join(', '),
          kids: s.kids,
          schoolClass: s.joinGroups,
          profilePic: s.photo,
        });
        set({ profileSaved: true });
      }

      /* The verify response carries the auth0 id; the JWT's sub is the fallback. */
      const userId = useAuthStore.getState().user?.id || getTokenSubject();
      let pending = false;

      /* Either join the class group that's already there or create one. Both
         end with the user recorded as a room parent of it. */
      let classNetworkId = s.classNetworkId;
      if (!classNetworkId) {
        if (s.joinClassId) {
          const res = await onboardingService.joinClassGroup({
            gradeNetworkId: s.roomParentGrade,
            classNetworkId: s.joinClassId,
            userId,
          });
          pending = res.pending;
          classNetworkId = s.joinClassId;
        } else {
          /* userId enrolls the creator in the new group — without it the class
             never shows up in their own contexts */
          const created = await onboardingService.createClassGroup({
            networkId: s.roomParentGrade,
            name: s.className.trim(),
            userId,
          });
          classNetworkId = created?.data?.networkId || '';
        }
        set({ classNetworkId });
      }

      if (classNetworkId) {
        await onboardingService.markRoomParent({ classNetworkId, userId });
      }

      /* login_step "3" is what the guards read — the stored user still carries
         the pre-onboarding value until the next profile fetch. */
      const { user, login } = useAuthStore.getState();
      login(null, { ...(user || {}), login_step: '3' });
      set({ submitting: false });
      return { pending };
    } catch (err) {
      set({ submitting: false });
      throw err;
    }
  },
}));

export default useSignupStore;
